import { Injectable } from '@angular/core';
import { ApiService, ApiCacheOptions } from '../../../shared/index';
import { ICreditCardGatewayService } from './icredit-card-gateway.service';
import { CreditCardGateway } from './credit-card-gateway.model';
import { CreditCardGatewayType } from './credit-card-gateway-type.dto';
import { CreditCardProcessorType } from './credit-card-processor-type.dto';

export interface AuthorizeDotNetPrecznCompatibilityResult {
  IsCompatible: boolean;
  CanConnectToGateway: boolean;
  RemoteAchEnabled: boolean;
  AchConsistentAcrossGateways: boolean;
  UsesAch: boolean;
  UsesArb: boolean;
  UsesPaymentProfiles: boolean;
  CardOnFileEnabled: boolean;
  HasSmartClientPayments: boolean;
  GatewayTypeId: number | null;
}

export interface CardPointePrecznCompatibilityResult {
  IsCompatible: boolean;
  CanConnectToGateway: boolean;
  RemoteAchEnabled: boolean;
  AchConsistentAcrossGateways: boolean;
  UsesArb: boolean;
  HasSmartClientPayments: boolean;
  GatewayTypeId: number | null;
}

@Injectable()
export class CreditCardGatewayService implements ICreditCardGatewayService {
  public _apiService: ApiService;
  private _cacheOptions: ApiCacheOptions;
  public constructor(apiService: ApiService) {
    this._apiService = apiService;
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
  }

  public getCreditCardGateways = (): Promise<Array<CreditCardGateway>> => {
    return this._apiService.get('EPayment/CreditCardGateways', this._cacheOptions);
  };

  public getCreditCardGatewaysSecure = (ignoreCache = false): Promise<Array<CreditCardGateway>> => {
    const cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
    cacheOptions.DisableCaching = ignoreCache;
    cacheOptions.DeleteCache = ignoreCache;
    return this._apiService.get('CreditCardGateways', cacheOptions);
  };

  public getCreditCardGatewaysByType = (gatewayTypeId: number): Promise<Array<CreditCardGateway>> => {
    return this._apiService.get('EPayment/CreditCardGateways/' + gatewayTypeId.toString(), this._cacheOptions);
  };

  public hasCardConnectCreditCardGateways = (): Promise<boolean> => {
    return this._apiService.get('EPayment/CreditCardGateways/CardConnect', this._cacheOptions).then((data: Array<CreditCardGateway>) => {
      if (data.length > 0) {
        return Promise.resolve(true);
      } else {
        return Promise.resolve(false);
      }
    });
  };

  public hasPrecznCreditCardGateways = (): Promise<boolean> => {
    return this.getCreditCardGatewaysByType(7).then((data: Array<CreditCardGateway>) => {
      return Promise.resolve(data.length > 0);
    });
  };

  public getCardConnectCreditCardGateways = (): Promise<Array<CreditCardGateway>> => {
    return this._apiService.get('EPayment/CreditCardGateways/CardConnect', this._cacheOptions);
  };

  public getAllCreditCardGateways(): Promise<Array<CreditCardGateway>> {
    return this._apiService.get('CreditCardGateways', this._cacheOptions);
  }

  create(ccGateway: CreditCardGateway): Promise<number> {
    return this._apiService.post('CreditCardGateway', ccGateway);
  }

  update(ccGateway: CreditCardGateway): Promise<void> {
    return this._apiService.put('CreditCardGateway', ccGateway);
  }

  delete(id: number): Promise<void> {
    return this._apiService.delete(`CreditCardGateway/${id}`);
  }

  checkAuthorizeDotNetPrecznCompatibility(gatewayId: number): Promise<AuthorizeDotNetPrecznCompatibilityResult> {
    return this._apiService.get(`CreditCardGateway/${gatewayId}/AuthorizeDotNetPrecznCompatibility`);
  }

  checkCardPointePrecznCompatibility(gatewayId: number): Promise<CardPointePrecznCompatibilityResult> {
    return this._apiService.get(`CreditCardGateway/${gatewayId}/CardPointePrecznCompatibility`);
  }

  checkAuthorizeDotNetPrecznCompatibilityForProposed(dto: CreditCardGateway): Promise<AuthorizeDotNetPrecznCompatibilityResult> {
    return this._apiService.post('CreditCardGateway/ProposedAuthorizeDotNetPrecznCompatibility', dto);
  }

  checkCardPointePrecznCompatibilityForProposed(dto: CreditCardGateway): Promise<CardPointePrecznCompatibilityResult> {
    return this._apiService.post('CreditCardGateway/ProposedCardPointePrecznCompatibility', dto);
  }
}

export function initGatewayTypes(): Array<CreditCardGatewayType> {
  return [
    { GatewayTypeId: 0, GatewayTypeName: 'None' },
    { GatewayTypeId: 1, GatewayTypeName: 'Authorize.Net (Legacy)' },
    { GatewayTypeId: 4, GatewayTypeName: 'Bambora' },
    { GatewayTypeId: 6, GatewayTypeName: 'CardPointe (Legacy)' },
    { GatewayTypeId: 7, GatewayTypeName: 'Momentive Payments Authorize.Net' },
    { GatewayTypeId: 8, GatewayTypeName: 'Momentive Payments CardPointe' },
    { GatewayTypeId: 3, GatewayTypeName: 'PayPal' },
  ];
}

const LEGACY_GATEWAY_TYPE_IDS = [1, 3, 4, 6];
const BASE_GATEWAY_TYPE_IDS = [0, 7, 8];

export function getAvailableGatewayTypes(ccGatewayId: number, currentGatewayTypeId: number): Array<CreditCardGatewayType> {
  const allGatewayTypes = initGatewayTypes();
  const baseSet = allGatewayTypes.filter((x) => BASE_GATEWAY_TYPE_IDS.includes(x.GatewayTypeId));

  if (ccGatewayId > 0 && LEGACY_GATEWAY_TYPE_IDS.includes(currentGatewayTypeId)) {
    const currentType = allGatewayTypes.find((x) => x.GatewayTypeId === currentGatewayTypeId);
    if (currentType) {
      return [...baseSet, currentType];
    }
  }

  return baseSet;
}

export function initPrecznProcessorTypes(): Array<CreditCardProcessorType> {
  return [
    { ProcessorTypeId: 0, ProcessorTypeName: 'None' },
    { ProcessorTypeId: 1, ProcessorTypeName: 'Authorize.Net' },
  ];
}
